<template>
  <div class="w-full flex justify-content-center">
    <div class="w-9">
      <div>
        <h3>Buscá, visualizá y descargá protocolos, leyes e instructivos desde un solo lugar.</h3>
      </div>
      <div>
        <p>Filtrar Documento</p>
        <div class="flex gap-2 align-items-center justify-content-between w-5">
          <MultiSelect
            :options="tiposDocumentos"
            v-model="tipoSelected"
            optionLabel="Nombre"
            key="CategoriatipoarchivopdfID"
            placeholder="Tipo de Archivo"
            style="border-radius: 6px"
            class="custom-focus flex-grow-1"
            display="chip"
          >
            <template #value="slotProps">
              <template v-if="!slotProps.value || slotProps.value.length === 0">
                Tipo de Archivo
              </template>
              <div v-if="slotProps.value.length <= 2">
                <Chip
                  v-for="(item, index) in slotProps.value"
                  :key="index"
                  :label="item.Nombre"
                  class="mr-1"
                  removable
                  @remove="quitarTipo(item)"
                />
              </div>
              <div v-else>
                <Chip :label="`${slotProps.value.length} seleccionados...`" />
              </div>
            </template>
          </MultiSelect>
          <InputText
            v-model="searchNombre"
            placeholder="Nombre de archivo"
            style="border-radius: 6px"
            class="custom-focus flex-grow-1"
          />
          <Button class="search-button flex-grow-0" @click="searchDoc">
            <div>
              <Search color="white" :size="20" />
            </div>
            Buscar
          </Button>
          <Button class="clean-button flex-grow-0" @click="limpiarFiltros">
            <div>
              <BrushCleaning color="#0B5195" :size="20" />
            </div>
            Limpiar
          </Button>
        </div>
      </div>
      <DataTable
        :value="paginatedArchivos"
        :loading="isLoading"
        :lazy="true"
        :first="offset"
        :rows="pageSize"
        :totalRecords="totalDocs"
        @page="handlePageChange"
        :resizableColumns="false"
        :paginator="false"
        class="center-screen mt-5 shadow-2"
        paginatorTemplate="PrevPageLink PageLinks NextPageLink"
        style="border-radius: 6px; min-height: 600px"
      >
        <template #empty> Aún no hay archivos registrados. </template>

        <Column field="Nombre" header="Nombre" />
        <Column field="Tipo" header="Tipo" />
        <Column field="Detalle" header="Detalle" />
        <Column header="Acción">
          <template #body="{ data }">
            <div class="w-full flex justify-content-center">
              <a
                :href="`${url}/${data.ArchivopdfID}?view=true`"
                target="_blank"
                class="no-underline"
              >
                <Button
                  class="seeButton"
                  :disabled="!data.ExistingFile"
                  :tooltipText="!data.ExistingFile ? 'Archivo no encontrado' : 'Visualizar archivo'"
                >
                  <div class="mt-1">
                    <Eye color="white" :size="20" />
                  </div>
                  Ver
                </Button>
              </a>
            </div>
          </template>
        </Column>
      </DataTable>
      <div class="flex justify-content-center mt-2" v-if="paginatedArchivos.length">
        <ul
          class="flex list-none border-round-xl overflow-hidden gap-2 border-gray px-3 text-custom-gray"
        >
          <li
            class="cursor-pointer px-3 py-2 my-1 border-gray hover:bg-gray-200 border-round-xl"
            @click="prevPage"
          >
            <i class="pi pi-angle-left"></i>
          </li>
          <li
            v-for="page in totalPages"
            :key="page"
            class="cursor-pointer px-3 py-2 my-1 border-gray hover:opacity-20 border-round-xl"
            :class="[page === currentPage ? 'selected' : 'hover:opacity-20']"
            @click="goToPage(page)"
          >
            {{ page }}
          </li>
          <li
            class="cursor-pointer px-3 py-2 my-1 border-gray hover:bg-gray-200 border-round-xl"
            @click="nextPage"
          >
            <i class="pi pi-angle-right"></i>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useBibliotecaStore } from "@/store/Pinia/useBibliotecaStore";
import { Eye, Search, BrushCleaning } from "lucide-vue";
import Chip from "primevue/chip";
import MultiSelect from "primevue/multiselect";

export default defineComponent({
  name: "BibliotecaVirtual",
  components: {
    Eye,
    Search,
    BrushCleaning,
    Chip,
    MultiSelect,
  },
  setup() {
    const searchNombre = ref("");
    const tipoSelected = ref([]);
    const url = ref(`${process.env.VUE_APP_BACKEND_ENVIRONMENT}/files/archivopdf`);
    const bibliotecaStore = useBibliotecaStore();
    const {
      paginatedArchivos,
      isLoading,
      pageSize,
      totalDocs,
      offset,
      handlePageChange,
      currentPage,
      totalPages,
      goToPage,
      prevPage,
      nextPage,
      tiposDocumentos,
    } = storeToRefs(bibliotecaStore);

    const searchDoc = () => {
      const tipos = tipoSelected.value.map((tipo) => tipo.Nombre);
      bibliotecaStore.filterDocs(searchNombre.value, tipos);
    };

    const limpiarFiltros = () => {
      searchNombre.value = "";
      tipoSelected.value = [];
      bibliotecaStore.filterDocs();
    };

    const quitarTipo = (item) => {
      tipoSelected.value = tipoSelected.value.filter((t) => t !== item);
      searchDoc();
    };

    onMounted(async () => {
      await bibliotecaStore.setDocumentos();
      await bibliotecaStore.setTiposDocumentos();
    });

    return {
      pageSize,
      paginatedArchivos,
      isLoading,
      totalDocs,
      offset,
      handlePageChange,
      currentPage,
      totalPages,
      goToPage,
      prevPage,
      nextPage,
      searchNombre,
      tiposDocumentos,
      tipoSelected,
      url,
      searchDoc,
      limpiarFiltros,
      quitarTipo,
    };
  },
});
</script>

<style scoped>
.clean-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-content: center !important;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--White-White, #fff);
  color: #0b5195;
  border: 1px solid #0b5195;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
}

.clean-button:hover {
  background: #f4f4f4 !important;
  color: #0b5195 !important;
  opacity: 0.9;
  cursor: pointer;
}

.search-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-content: center !important;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #0b5195;
  color: #ffffff;
  border: 1px solid #0b5195;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
}

.search-button:hover {
  background: #0b5195 !important;
  color: #f4f4f4 !important;
  opacity: 0.9;
  cursor: pointer;
}

.seeButton {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: var(--Primary, #0b5195);
  color: white;
  border: none;
}

.seeButton:hover {
  opacity: 0.9;
  cursor: pointer;
}

.center-screen {
  margin: 0 auto 1rem auto;
}

.border-gray {
  border: 1px solid #dfe4ea;
}

.selected {
  background-color: #3758f9;
  color: white;
}

.text-custom-gray {
  color: #637381;
}

.custom-focus:focus-within {
  border: 1.5px solid #3758f9;
}

>>> .p-datatable .p-column-header-content {
  justify-content: center;
}

>>> .p-datatable .p-datatable-tbody > tr > td {
  text-align: center;
}

>>> .p-datatable-table {
  table-layout: fixed;
}

>>> .p-datatable-wrapper .p-datatable-table {
  min-height: 100%;
}
</style>
